/**
 * The paging half of the shared `react-select` control (`docs/select-inputs.md` §4, ADR-0024).
 *
 * A select whose options live on the server cannot be handed to `Select` as an array: the control opens with one page,
 * searches on the server and asks for the next page when its menu is scrolled to the bottom. That contract is this hook and
 * the types beside it; `react-select.tsx` is the component that renders what the hook returns.
 *
 * It is a separate module because of where the SPA's fast-refresh rule draws the line: a `.tsx` file may export components
 * and nothing else, and a hook exported from one makes every hot update of the control remount it. The state the hook owns,
 * and the reasons it is shaped this way, are documented on {@link useAsyncSelectOptions}.
 *
 * @see ADR-0024 — one select implementation for the whole SPA.
 * @see TASK-0056 — the category rail, the first list long enough to need this.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import type { SelectOption } from '@/components/ui/select-options';

/**
 * The first page a control backed by the server loads, and the size of every page after it.
 *
 * `docs/select-inputs.md` §4 asks a list held by the API to open with no more than five rows, so the field is usable
 * immediately and the rest arrives as the user asks for it. The size lives here rather than at the call site because the
 * control — not the screen — is what turns a menu scroll into a request.
 */
export const ASYNC_SELECT_PAGE_SIZE = 5;

/** One page of options as the server answers it. */
export interface AsyncSelectPage {
  /** The rows of this page, already projected to options. */
  options: SelectOption[];
  /** Whether a page exists after this one; the menu asks for it when it is scrolled to the bottom. */
  hasNextPage: boolean;
}

/**
 * Receives one page of options.
 *
 * `search` is the text the user typed, already trimmed; `page` is one-based. A call must be able to run twice for the
 * same arguments — typing, scrolling back and re-opening the menu all ask again — so the caller keeps no cursor.
 */
export type AsyncSelectLoader = (request: {
  /** Trimmed search text, `''` when the user is only browsing. */
  search: string;
  /** One-based page number. */
  page: number;
  /** Rows requested per page, always {@link ASYNC_SELECT_PAGE_SIZE}. */
  pageSize: number;
}) => Promise<AsyncSelectPage>;

/**
 * The rows, the paging position and the failure of one list the server holds.
 *
 * The state is kept in one object rather than in three `useState` calls because every transition changes more than one of
 * them — a new search empties the rows *and* rewinds the page — and two `setState` calls that describe one transition are
 * a half-applied state if anything renders between them.
 */
export interface AsyncSelectState {
  /** Rows loaded so far, in the server's order, without duplicates. */
  options: SelectOption[];
  /** Search text the rows belong to; a page appended under another text is dropped. */
  search: string;
  /** Page held. */
  page: number;
  /** Whether a page exists after {@link page}. */
  hasNextPage: boolean;
  /** Whether the first page of the current search is in flight. */
  isLoading: boolean;
  /** Whether a later page is in flight. */
  isLoadingMore: boolean;
  /** The failure of the last request, or `null` after a success. */
  error: unknown;
}

const EMPTY_ASYNC_STATE: AsyncSelectState = {
  options: [],
  search: '',
  page: 0,
  hasNextPage: false,
  isLoading: false,
  isLoadingMore: false,
  error: null,
};

/**
 * How long typing pauses before the control searches, in milliseconds.
 *
 * Long enough that a typed word is one request rather than one per letter, short enough that the answer is on screen before
 * the user looks for it. Persian is typed letter by letter on most keyboards, so requests are debounced rather than sent
 * per keystroke (`docs/select-inputs.md` §4).
 */
const ASYNC_SELECT_SEARCH_DEBOUNCE_MS = 300;

/**
 * Appends a page to the rows held, skipping a row whose identifier is already there.
 *
 * The server orders rows by a presentation key, which an administrator can change between two requests; a row can then cross
 * a page boundary and be answered twice. A duplicate identifier would give `react-select` two options with one value, and the
 * second would win the rendering of the label. The page itself is de-duplicated too (TASK-0057): a list read with skip/limit
 * over a key an edit is moving can repeat a row *within* one answer, which no boundary check would catch.
 *
 * @param current Rows held.
 * @param next Rows of the new page.
 * @returns The rows held followed by the new ones, in order, without a repeated identifier.
 */
function appendOptions(
  current: readonly SelectOption[],
  next: readonly SelectOption[],
): SelectOption[] {
  const seen = new Set(current.map((option) => option.value));

  return [
    ...current,
    ...next.filter((option) => {
      if (seen.has(option.value)) {
        return false;
      }

      seen.add(option.value);
      return true;
    }),
  ];
}

/**
 * Reads a list from the server a page at a time (`docs/select-inputs.md` §4).
 *
 * The contract the control keeps, whatever the caller's loader does:
 *
 * - **the first page loads on its own.** A field that opened empty until the user typed would hide the answer that is
 *   already there, so the control asks for page one once, with no search text;
 * - **typing searches, it does not filter.** Every change of text asks the server again from page one — the rows the
 *   control holds are one page of a list it has never seen whole, so filtering them locally would answer with a subset of a
 *   subset. The text is debounced so a word is one request rather than one per keystroke;
 * - **scrolling asks for the next page.** The menu's scroll-to-bottom asks for `page + 1` when the server said one exists,
 *   and the answer is appended in the server's order;
 * - **a late answer is dropped.** Answers carry the number of the request that asked for them, so a page that arrives after
 *   the user has typed again — or after the list stopped being wanted — cannot append rows under another search term;
 * - **a failure is a state, not an exception.** The menu shows the error and a retry that repeats the request that failed,
 *   so an outage costs one row of the menu rather than the form around it.
 *
 * @param load Receives one page; read through a ref, so a caller may pass a fresh arrow function on every render.
 * @param enabled Whether the list is wanted at all. A dependent field whose parent is unchosen passes `false` and makes no
 * request, which is what keeps "no parent, no child list" from being a request the server has to answer.
 * @returns The rows loaded so far, the search text held, and the handlers the menu calls.
 */
export function useAsyncSelectOptions(load: AsyncSelectLoader, enabled: boolean) {
  // One state object, one source of truth, for the list *and* the text it belongs to (TASK-0057). The text used to live in a
  // second `useState` and be reported separately, which meant the control's input and the rows on screen were two facts that
  // could disagree: closing a menu whose search found the empty answers to the old term. Now the text the menu shows and the
  // term the request was made for are the same member, so the control cannot render one while holding the other.
  const [state, setState] = useState<AsyncSelectState>(EMPTY_ASYNC_STATE);

  const loadRef = useRef(load);
  loadRef.current = load;

  // Number of the request whose answer is still wanted. Every request takes the next number and only stores its answer if
  // that number is still the current one.
  const requestSeq = useRef(0);
  // The single timer of the debounce, so a keystroke cancels the request the previous one scheduled.
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The page a retry repeats; `null` when nothing failed.
  const failedPage = useRef<number | null>(null);
  // The page currently in flight, or `null`. A ref rather than the state's flags because two gestures can arrive inside one
  // render: a trackpad reports the end of a list several times before React has committed the first answer, and a guard that
  // reads rendered state would let each one through (TASK-0057).
  const inFlightPage = useRef<number | null>(null);

  /** Runs one page request, replacing the rows at page one and appending after it. */
  const fetchPage = useCallback((term: string, page: number) => {
    const seq = ++requestSeq.current;
    failedPage.current = null;
    inFlightPage.current = page;

    setState((current) => ({
      ...current,
      search: term,
      // The position is recorded as soon as the request leaves, not when it lands (TASK-0057). A menu scrolled to its bottom
      // while page one is still in flight would otherwise read `page: 0` and ask for page one a second time, which is the
      // duplicate request the footer's scroll hint invites.
      page,
      // The first page is a new list: the previous rows go at once, so a search never shows the old answers under the new
      // text. A later page keeps them and is shown as loading underneath.
      options: page === 1 ? [] : current.options,
      hasNextPage: page === 1 ? false : current.hasNextPage,
      isLoading: page === 1,
      isLoadingMore: page > 1,
      error: null,
    }));

    void loadRef
      .current({ search: term, page, pageSize: ASYNC_SELECT_PAGE_SIZE })
      .then((answer) => {
        // A superseded answer — the user typed, the parent changed, a retry overtook it, or the menu closed and went back to
        // its first page — is discarded silently. Its slot is released only if it is still the request occupying it.
        if (inFlightPage.current === page) {
          inFlightPage.current = null;
        }

        if (seq !== requestSeq.current) {
          return;
        }

        setState((current) => ({
          ...current,
          options: page === 1 ? answer.options : appendOptions(current.options, answer.options),
          page,
          hasNextPage: answer.hasNextPage,
          isLoading: false,
          isLoadingMore: false,
          error: null,
        }));
      })
      .catch((error: unknown) => {
        // A failure of an abandoned request releases nothing: the slot belongs to whichever request is current now.
        if (inFlightPage.current === page) {
          inFlightPage.current = null;
        }

        if (seq !== requestSeq.current) {
          return;
        }

        failedPage.current = page;
        setState((current) => ({
          ...current,
          // A failed first page leaves no rows to read; a failed later page keeps the ones already loaded, because they are
          // still true and the next scroll asks again. The position is kept in both cases (TASK-0057): it is where the user
          // had reached, and it is what the retry repeats.
          options: page === 1 ? [] : current.options,
          isLoading: false,
          isLoadingMore: false,
          error,
        }));
      });
  }, []);

  // The first page, once the list is wanted. Re-running when `enabled` flips is deliberate: a dependent field that gets its
  // parent makes its first request here, and one that loses its parent stops showing another branch's rows.
  //
  // Opening on page one with no term is also what the menu does when it is reopened (TASK-0057): a menu that reopens with an
  // empty input shows the unfiltered first page, so the request is made when the list becomes wanted and whenever the text
  // changes — never one term's rows left standing under another term's input.
  useEffect(() => {
    if (!enabled) {
      requestSeq.current += 1;
      setState(EMPTY_ASYNC_STATE);
      return undefined;
    }

    fetchPage('', 1);

    return () => {
      // The component unmounted or the list stopped being wanted: an answer still on its way must not reach a new list.
      requestSeq.current += 1;
    };
  }, [enabled, fetchPage]);

  // The debounce timer belongs to the component rather than to a keystroke, so it is cleared when there is no component.
  useEffect(
    () => () => {
      if (debounce.current !== null) {
        clearTimeout(debounce.current);
      }
    },
    [],
  );

  /**
   * Takes the text the menu shows, as a new search from page one.
   *
   * The text is stored immediately — it is what the input displays — while the request that answers it waits for the pause in
   * typing. Keeping the typed text and the requested term in one transition means the two can never describe different terms.
   */
  const onSearch = useCallback(
    (text: string) => {
      setState((current) => ({ ...current, search: text }));

      if (debounce.current !== null) {
        clearTimeout(debounce.current);
      }

      debounce.current = setTimeout(() => {
        debounce.current = null;
        fetchPage(text.trim(), 1);
      }, ASYNC_SELECT_SEARCH_DEBOUNCE_MS);
    },
    [fetchPage],
  );

  /**
   * Goes back to the unfiltered first page, at once.
   *
   * This is the menu's own close (TASK-0057): the control clears its input when the menu shuts, so the next opening shows an
   * empty field — and an empty field must show the first page rather than the rows of a term that is no longer on screen. It
   * cancels a search still being typed and leaves an in-flight request to be dropped by its sequence number, rather than
   * having the answer of the abandoned term land later.
   */
  const onReset = useCallback(() => {
    if (debounce.current !== null) {
      clearTimeout(debounce.current);
      debounce.current = null;
    }

    fetchPage('', 1);
  }, [fetchPage]);

  /** Asks for the page after the one held, when the server said there is one. */
  const onLoadMore = useCallback(() => {
    if (
      // A page is still on its way. Read from the ref, not from the render, because a burst of scroll gestures arrives before
      // any of them is answered, and each one must not become a request of its own.
      inFlightPage.current !== null ||
      // Nothing was asked for yet, so there is no page to follow: asking here would be a second request for page one.
      state.page === 0 ||
      state.hasNextPage !== true
    ) {
      return;
    }

    fetchPage(state.search.trim(), state.page + 1);
  }, [fetchPage, state.hasNextPage, state.page, state.search]);

  /** Repeats the request that failed, keeping the page the user had reached. */
  const onRetry = useCallback(() => {
    fetchPage(state.search.trim(), failedPage.current ?? 1);
  }, [fetchPage, state.search]);

  return {
    /** Rows loaded so far, in the server's order. */
    options: state.options,
    /** Search text the menu shows, which is what the rows belong to. */
    search: state.search,
    /** Whether a page is in flight. */
    isLoading: state.isLoading || state.isLoadingMore,
    /** Whether a later page is in flight. */
    isLoadingMore: state.isLoadingMore,
    /** Whether the server said a page exists after the rows held. */
    hasNextPage: state.hasNextPage,
    /** The failure of the last request, or `null`. */
    error: state.error,
    /** Reports typed text; the request follows once typing pauses. */
    onSearch,
    /** Asks for the next page; the menu calls it when scrolled to the bottom. */
    onLoadMore,
    /** Returns to the unfiltered first page; the menu calls it when it closes. */
    onReset,
    /** Repeats the failed request. */
    onRetry,
  };
}
